import { useMemo, useState } from "react";
import { replayCategories, replayVideos } from "@/mocks/home";
import SectionHeading from "@/pages/home/components/SectionHeading";

interface ReplaySectionProps {
  activeCategory: string;
  onCategoryChange: (category: string) => void;
}

export default function ReplaySection({
  activeCategory,
  onCategoryChange,
}: ReplaySectionProps) {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return replayVideos.filter((item) => {
      const matchCategory =
        activeCategory === "全部" || item.category === activeCategory;
      const matchQuery =
        q.length === 0 ||
        item.title.toLowerCase().includes(q) ||
        item.streamer.toLowerCase().includes(q) ||
        item.tags.some((tag) => tag.toLowerCase().includes(q));
      return matchCategory && matchQuery;
    });
  }, [activeCategory, query]);

  return (
    <section id="replay" className="py-16 md:py-24">
      <div className="mx-auto max-w-page px-4 md:px-6">
        <SectionHeading
          eyebrow="Replay Library"
          title="热门主播的直播回放"
          desc="错过直播也不用担心。这里按分类整理了近期上线的主播回放，每条都标注了主播、分类、时长与上线日期，点选分类或搜索关键词即可快速定位。"
        />

        <div
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-8"
          data-reveal
        >
          <div className="flex flex-wrap items-center gap-2">
            {replayCategories.map((cat) => {
              const active = cat === activeCategory;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => onCategoryChange(cat)}
                  aria-pressed={active}
                  className={`rounded-full px-3.5 py-1.5 text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    active
                      ? "bg-primary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {cat}
                </button>
              );
            })}
          </div>

          <div className="relative w-full md:w-72 shrink-0">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索标题、主播或标签"
              aria-label="搜索直播回放"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-10 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>
        </div>

        <p className="mb-5 text-xs md:text-sm text-foreground-500" data-reveal>
          当前筛选：
          <span className="text-foreground-800 font-medium">{activeCategory}</span>
          {query.trim() ? ` · 关键词「${query.trim()}」` : ""}
          ，共
          <span className="font-num text-primary-600 font-bold mx-1">{filtered.length}</span>
          场回放
        </p>

        {filtered.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center" data-reveal>
            <span className="mx-auto mb-3 w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xl">
              <i className="ri-inbox-line" />
            </span>
            <p className="text-sm text-foreground-700">
              没有找到符合条件的回放，换个分类或关键词再试试。
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {filtered.map((item) => (
              <article
                key={item.id}
                data-reveal
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 transition-colors hover:border-primary-300"
              >
                <div className="relative w-full aspect-[16/9] overflow-hidden">
                  <img
                    src={item.image}
                    alt={`${item.streamer} 的直播回放封面：${item.title}`}
                    className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                  />
                  <span className="absolute bottom-3 right-3 rounded bg-black/60 px-2 py-0.5 font-num text-[11px] text-white">
                    {item.duration}
                  </span>
                  {item.hot ? (
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50">
                      <i className="ri-fire-line" />
                      热门
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2 text-xs text-foreground-500">
                    <span className="rounded-full bg-accent-100 px-2 py-0.5 font-medium text-accent-800">
                      {item.category}
                    </span>
                    <span>{item.date} 上线</span>
                  </div>
                  <h3 className="mt-2.5 text-sm md:text-base font-semibold leading-snug text-foreground-950 line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="mt-2 text-xs md:text-sm leading-relaxed text-foreground-600 line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3">
                    <span className="flex items-center gap-2 text-xs text-foreground-700">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-secondary-200 text-[11px] font-medium text-secondary-900">
                        {item.streamer.slice(0, 1)}
                      </span>
                      {item.streamer}
                    </span>
                    <span className="flex items-center gap-1 font-num text-xs text-foreground-500">
                      <i className="ri-eye-line" />
                      {item.views}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}